/** HTML fragments for Categories / Tags model-eval panels (mirrors Title layout). */

export function getTaxonomySuggestionCellHtml(
    bindingPath: string,
    kind: "category" | "tag"
): string {
    const itemBody = kind === "category"
        ? `
            <div style="font-weight: 600; white-space: pre-wrap;" data-bind="text: name"></div>
            <!-- ko if: id -->
            <div class="form-sub-title" style="margin-top: 2px;" data-bind="text: '[ID: ' + id + ']'"></div>
            <!-- /ko -->`
        : `
            <div style="font-weight: 600; white-space: pre-wrap;">
                <span data-bind="text: name"></span>
                <!-- ko if: tagType -->
                <span data-bind="text: ' (' + tagType + ')'"></span>
                <!-- /ko -->
            </div>
            <!-- ko if: id -->
            <div class="form-sub-title" style="margin-top: 2px;" data-bind="text: '[ID: ' + id + ']'"></div>
            <!-- /ko -->`;

    return /* html */`
        <td data-bind="with: ${bindingPath}">
            <!-- ko if: status() === 'idle' -->
                <span class="form-sub-title">—</span>
            <!-- /ko -->
            <!-- ko if: status() === 'loading' -->
                <span>Running…</span>
            <!-- /ko -->
            <!-- ko if: status() === 'error' -->
                <div style="color: #b00020;">
                    <strong>Error</strong>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <pre style="white-space: pre-wrap; margin: 4px 0 0; font-size: 11px;" data-bind="text: error"></pre>
                    <!-- ko if: rawJson -->
                    <pre style="white-space: pre-wrap; margin: 6px 0 0; font-size: 11px; max-height: 160px; overflow: auto;" data-bind="text: rawJson"></pre>
                    <!-- /ko -->
                </div>
            <!-- /ko -->
            <!-- ko if: status() === 'done' -->
                <div>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <!-- ko if: costLabel -->
                    <div class="form-sub-title" data-bind="text: costLabel"></div>
                    <!-- /ko -->
                    <ol style="margin: 6px 0 0; padding-left: 1.2rem; font-size: 12px;">
                        <!-- ko foreach: suggestions -->
                        <li style="margin-bottom: 10px;">${itemBody}
                        </li>
                        <!-- /ko -->
                    </ol>
                </div>
            <!-- /ko -->
        </td>
    `;
}

function modelColumns(getCell: (path: string) => string): string {
    return `
        ${getCell("gpt5_nano")}
        ${getCell("gpt4o_mini")}
        ${getCell("gpt5_mini")}
    `;
}

function factCheckModelColumns(getCell: (path: string) => string): string {
    return `
        ${getCell("perplexity_sonar")}
        ${getCell("gpt5_nano")}
        ${getCell("gpt4o_mini")}
        ${getCell("gpt5_mini")}
    `;
}



export function getCategoryPanelHtml(): string {
    const cell = (p: string) => getTaxonomySuggestionCellHtml(p, "category");
    return /* html */`
        <div data-bind="visible: selectedArticleAssistantTab() == 'categories'">
            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-category-brand">Brand</label>
                <div class="w-input" style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
                    <select id="model-eval-category-brand" class="input-text"
                        data-bind="options: excerptBrands,
                                   optionsText: 'name',
                                   optionsValue: 'id',
                                   value: categoryIdBrand,
                                   optionsCaption: 'Select a brand',
                                   event: { change: onChangeCategoryBrand },
                                   enable: !isRunning()"
                        style="width: 100%; max-width: 420px;"></select>
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickFetchCategoryFixtures, enable: !isRunning() && !!categoryIdBrand()">
                        Fetch
                    </button>
                </div>
                <div class="form-sub-title" style="margin-top: 6px;">
                    Fetch loads article fixtures and builds the production categorization prompt from this brand’s active categories.
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-category-system-prompt">System prompt</label>
                <div class="form-sub-title" style="margin-bottom: 6px;">
                    Production categorize prompt with brand taxonomy embedded. Editable for testing.
                </div>
                <div class="w-input">
                    <textarea id="model-eval-category-system-prompt" class="input-text" rows="12"
                        data-bind="value: categorySystemPrompt"
                        style="width: 100%; min-height: 220px; font-family: monospace; font-size: 12px;"></textarea>
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-category-custom-title">Custom article title</label>
                <div class="w-input">
                    <input id="model-eval-category-custom-title" class="input-text" type="text"
                        data-bind="value: categoryCustomTitle" placeholder="Paste an article title" style="width: 100%;" />
                </div>
                <label class="form-label" for="model-eval-category-custom-content" style="margin-top: 8px;">Custom article content</label>
                <div class="w-input">
                    <textarea id="model-eval-category-custom-content" class="input-text" rows="6"
                        data-bind="value: categoryCustomContent"
                        placeholder="Paste article content to evaluate against all models"
                        style="width: 100%; min-height: 120px;"></textarea>
                </div>
                <div class="list-header w-justify" style="margin-top: 8px;">
                    <div class="w-left">
                        <button class="btn highlight" type="button"
                            data-bind="click: onClickRunCategoryCustom, enable: !isRunning() && !!categoryCustomTitle() && !!categoryCustomContent()">
                            Run custom categories
                        </button>
                    </div>
                    <div class="w-center">
                        <span data-bind="text: categoryCustomRunStatus"></span>
                    </div>
                </div>
                <div class="w-list-results" style="margin-top: 8px;">
                    <table class="list-results">
                        <thead>
                            <tr>
                                <th style="width: 28%;"><span>Custom Article</span></th>
                                <th style="width: 24%;"><span>GPT-5 nano</span></th>
                                <th style="width: 24%;"><span>GPT-4o mini</span></th>
                                <th style="width: 24%;"><span>GPT-5 mini</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr data-bind="with: categoryCustomRow">
                                <td>
                                    <div data-bind="text: title" style="font-weight: 600;"></div>
                                    <div class="form-sub-title" style="margin-top: 6px; white-space: pre-wrap;" data-bind="text: contentPreview"></div>
                                </td>
                                ${modelColumns(cell)}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="list-header w-justify">
                <div class="w-left">
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickRunCategoryAll, enable: !isRunning() && !!categoryIdBrand() && categoryRows().length > 0">
                        Run all
                    </button>
                    <button class="btn cancel" type="button" style="margin-left: 8px;"
                        data-bind="click: onClickStop, enable: isRunning()">
                        Stop
                    </button>
                </div>
                <div class="w-center">
                    <span data-bind="text: categoryRunStatus"></span>
                </div>
                <div class="w-right">
                    <span data-bind="text: categoryRows().length + ' fixtures'"></span>
                </div>
            </div>

            <div class="w-list-results" id="model-eval-category-results">
                <table class="list-results">
                    <thead>
                        <tr>
                            <th style="width: 28%;"><span>Article</span></th>
                            <th style="width: 24%;"><span>GPT-5 nano</span></th>
                            <th style="width: 24%;"><span>GPT-4o mini</span></th>
                            <th style="width: 24%;"><span>GPT-5 mini</span></th>
                        </tr>
                    </thead>
                    <tbody data-bind="foreach: categoryRows">
                        <tr>
                            <td>
                                <div data-bind="text: title" style="font-weight: 600;"></div>
                                <!-- ko if: permalink -->
                                <div style="margin-top: 6px;">
                                    <a data-bind="attr: { href: permalink, title: permalink }, text: permalink"
                                        target="_blank" rel="noopener noreferrer"
                                        style="font-size: 11px; text-transform: lowercase; word-break: break-all;"></a>
                                </div>
                                <!-- /ko -->
                            </td>
                            ${modelColumns(cell)}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `;
}

export function getTagPanelHtml(): string {
    const cell = (p: string) => getTaxonomySuggestionCellHtml(p, "tag");
    return /* html */`
        <div data-bind="visible: selectedArticleAssistantTab() == 'tags'">
            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-tag-brand">Brand</label>
                <div class="w-input" style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
                    <select id="model-eval-tag-brand" class="input-text"
                        data-bind="options: excerptBrands,
                                   optionsText: 'name',
                                   optionsValue: 'id',
                                   value: tagIdBrand,
                                   optionsCaption: 'Select a brand',
                                   event: { change: onChangeTagBrand },
                                   enable: !isRunning()"
                        style="width: 100%; max-width: 420px;"></select>
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickFetchTagFixtures, enable: !isRunning() && !!tagIdBrand()">
                        Fetch
                    </button>
                </div>
                <div class="form-sub-title" style="margin-top: 6px;">
                    Tag evaluation extracts terms, then ranks them against matched brand tags.
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-tag-step1-prompt">Step 1 system prompt (term extraction)</label>
                <div class="w-input">
                    <textarea id="model-eval-tag-step1-prompt" class="input-text" rows="6"
                        data-bind="value: tagStep1SystemPrompt"
                        style="width: 100%; min-height: 120px; font-family: monospace; font-size: 12px;"></textarea>
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-tag-step2-prompt">Step 2 system prompt template</label>
                <div class="form-sub-title" style="margin-bottom: 6px;">
                    Use <code>{{TAG_LIST}}</code> where the matched candidate tags JSON should be injected (production behavior).
                </div>
                <div class="w-input">
                    <textarea id="model-eval-tag-step2-prompt" class="input-text" rows="10"
                        data-bind="value: tagStep2SystemPromptTemplate"
                        style="width: 100%; min-height: 180px; font-family: monospace; font-size: 12px;"></textarea>
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-tag-custom-title">Custom article title</label>
                <div class="w-input">
                    <input id="model-eval-tag-custom-title" class="input-text" type="text"
                        data-bind="value: tagCustomTitle" placeholder="Paste an article title" style="width: 100%;" />
                </div>
                <label class="form-label" for="model-eval-tag-custom-content" style="margin-top: 8px;">Custom article content</label>
                <div class="w-input">
                    <textarea id="model-eval-tag-custom-content" class="input-text" rows="6"
                        data-bind="value: tagCustomContent"
                        placeholder="Paste article content to evaluate against all models"
                        style="width: 100%; min-height: 120px;"></textarea>
                </div>
                <div class="list-header w-justify" style="margin-top: 8px;">
                    <div class="w-left">
                        <button class="btn highlight" type="button"
                            data-bind="click: onClickRunTagCustom, enable: !isRunning() && !!tagCustomTitle() && !!tagCustomContent()">
                            Run custom tags
                        </button>
                    </div>
                    <div class="w-center">
                        <span data-bind="text: tagCustomRunStatus"></span>
                    </div>
                </div>
                <div class="w-list-results" style="margin-top: 8px;">
                    <table class="list-results">
                        <thead>
                            <tr>
                                <th style="width: 28%;"><span>Custom Article</span></th>
                                <th style="width: 24%;"><span>GPT-5 nano</span></th>
                                <th style="width: 24%;"><span>GPT-4o mini</span></th>
                                <th style="width: 24%;"><span>GPT-5 mini</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr data-bind="with: tagCustomRow">
                                <td>
                                    <div data-bind="text: title" style="font-weight: 600;"></div>
                                    <div class="form-sub-title" style="margin-top: 6px; white-space: pre-wrap;" data-bind="text: contentPreview"></div>
                                </td>
                                ${modelColumns(cell)}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="list-header w-justify">
                <div class="w-left">
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickRunTagAll, enable: !isRunning() && !!tagIdBrand() && tagRows().length > 0">
                        Run all
                    </button>
                    <button class="btn cancel" type="button" style="margin-left: 8px;"
                        data-bind="click: onClickStop, enable: isRunning()">
                        Stop
                    </button>
                </div>
                <div class="w-center">
                    <span data-bind="text: tagRunStatus"></span>
                </div>
                <div class="w-right">
                    <span data-bind="text: tagRows().length + ' fixtures'"></span>
                </div>
            </div>

            <div class="w-list-results" id="model-eval-tag-results">
                <table class="list-results">
                    <thead>
                        <tr>
                            <th style="width: 28%;"><span>Article</span></th>
                            <th style="width: 24%;"><span>GPT-5 nano</span></th>
                            <th style="width: 24%;"><span>GPT-4o mini</span></th>
                            <th style="width: 24%;"><span>GPT-5 mini</span></th>
                        </tr>
                    </thead>
                    <tbody data-bind="foreach: tagRows">
                        <tr>
                            <td>
                                <div data-bind="text: title" style="font-weight: 600;"></div>
                                <!-- ko if: permalink -->
                                <div style="margin-top: 6px;">
                                    <a data-bind="attr: { href: permalink, title: permalink }, text: permalink"
                                        target="_blank" rel="noopener noreferrer"
                                        style="font-size: 11px; text-transform: lowercase; word-break: break-all;"></a>
                                </div>
                                <!-- /ko -->
                            </td>
                            ${modelColumns(cell)}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `;
}

export function getThreadSuggestionCellHtml(bindingPath: string): string {
    return /* html */`
        <td data-bind="with: ${bindingPath}">
            <!-- ko if: status() === 'idle' -->
                <span class="form-sub-title">—</span>
            <!-- /ko -->
            <!-- ko if: status() === 'loading' -->
                <span>Running…</span>
            <!-- /ko -->
            <!-- ko if: status() === 'error' -->
                <div style="color: #b00020;">
                    <strong>Error</strong>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <pre style="white-space: pre-wrap; margin: 4px 0 0; font-size: 11px;" data-bind="text: error"></pre>
                    <!-- ko if: rawJson -->
                    <pre style="white-space: pre-wrap; margin: 6px 0 0; font-size: 11px; max-height: 160px; overflow: auto;" data-bind="text: rawJson"></pre>
                    <!-- /ko -->
                </div>
            <!-- /ko -->
            <!-- ko if: status() === 'done' -->
                <div>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <!-- ko if: costLabel -->
                    <div class="form-sub-title" data-bind="text: costLabel"></div>
                    <!-- /ko -->
                    <ol style="margin: 6px 0 0; padding-left: 1.2rem; font-size: 12px;">
                        <!-- ko foreach: suggestions -->
                        <li style="margin-bottom: 12px;">
                            <div style="font-weight: 600; white-space: pre-wrap;">
                                <span class="form-sub-title">Prompt:</span>
                                <span data-bind="text: question"></span>
                            </div>
                            <!-- ko if: answer -->
                            <div class="form-sub-title" style="margin-top: 4px; white-space: pre-wrap;">
                                <strong>Prompt Response:</strong>
                                <span data-bind="text: answer"></span>
                            </div>
                            <!-- /ko -->
                        </li>
                        <!-- /ko -->
                    </ol>
                </div>
            <!-- /ko -->
        </td>
    `;
}

export function getThreadPanelHtml(): string {
    const cell = (p: string) => getThreadSuggestionCellHtml(p);
    return /* html */`
        <div data-bind="visible: selectedArticleAssistantTab() == 'thread_prompt'">
            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-thread-brand">Brand</label>
                <div class="w-input" style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
                    <select id="model-eval-thread-brand" class="input-text"
                        data-bind="options: excerptBrands,
                                   optionsText: 'name',
                                   optionsValue: 'id',
                                   value: threadIdBrand,
                                   optionsCaption: 'Select a brand',
                                   event: { change: onChangeThreadBrand },
                                   enable: !isRunning()"
                        style="width: 100%; max-width: 420px;"></select>
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickFetchThreadFixtures, enable: !isRunning() && !!threadIdBrand()">
                        Fetch
                    </button>
                </div>
                <div class="form-sub-title" style="margin-top: 6px;">
                    Fetch loads published articles with body content using this brand’s AI Config Thread filters
                    (<code>selectedThreadContentTypes</code> / <code>selectedThreadArticleTypes</code>), excluding Thread template articles.
                    Prompt comes from <code>threadSuggestionPrompt</code>. Production returns Prompt + Prompt Response in one call.
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-thread-system-prompt">System prompt</label>
                <div class="form-sub-title" style="margin-bottom: 6px;">
                    Loaded from the selected brand’s AI Config. Editable for testing.
                </div>
                <div class="w-input">
                    <textarea id="model-eval-thread-system-prompt" class="input-text" rows="12"
                        data-bind="value: threadSystemPrompt"
                        style="width: 100%; min-height: 220px; font-family: monospace; font-size: 12px;"></textarea>
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-thread-custom-title">Custom article title</label>
                <div class="w-input">
                    <input id="model-eval-thread-custom-title" class="input-text" type="text"
                        data-bind="value: threadCustomTitle"
                        placeholder="Paste an article title"
                        style="width: 100%;" />
                </div>
                <label class="form-label" for="model-eval-thread-custom-content" style="margin-top: 8px;">Custom article content</label>
                <div class="w-input">
                    <textarea id="model-eval-thread-custom-content" class="input-text" rows="6"
                        data-bind="value: threadCustomContent"
                        placeholder="Paste article content to evaluate against all models"
                        style="width: 100%; min-height: 120px;"></textarea>
                </div>
                <div class="list-header w-justify" style="margin-top: 8px;">
                    <div class="w-left">
                        <button class="btn highlight" type="button"
                            data-bind="click: onClickRunThreadCustom, enable: !isRunning() && !!threadCustomTitle() && !!threadCustomContent()">
                            Run custom thread
                        </button>
                    </div>
                    <div class="w-center">
                        <span data-bind="text: threadCustomRunStatus"></span>
                    </div>
                </div>
                <div class="w-list-results" style="margin-top: 8px;">
                    <table class="list-results">
                        <thead>
                            <tr>
                                <th style="width: 28%;"><span>Custom Article</span></th>
                                <th style="width: 24%;"><span>GPT-5 nano</span></th>
                                <th style="width: 24%;"><span>GPT-4o mini</span></th>
                                <th style="width: 24%;"><span>GPT-5 mini</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr data-bind="with: threadCustomRow">
                                <td>
                                    <div data-bind="text: title" style="font-weight: 600;"></div>
                                    <div class="form-sub-title" style="margin-top: 6px; white-space: pre-wrap;" data-bind="text: contentPreview"></div>
                                </td>
                                ${modelColumns(cell)}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="list-header w-justify">
                <div class="w-left">
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickRunThreadAll, enable: !isRunning() && !!threadIdBrand() && threadRows().length > 0">
                        Run all
                    </button>
                    <button class="btn cancel" type="button" style="margin-left: 8px;"
                        data-bind="click: onClickStop, enable: isRunning()">
                        Stop
                    </button>
                </div>
                <div class="w-center">
                    <span data-bind="text: threadRunStatus"></span>
                </div>
                <div class="w-right">
                    <span data-bind="text: threadRows().length + ' fixtures'"></span>
                </div>
            </div>

            <div class="w-list-results" id="model-eval-thread-results">
                <table class="list-results">
                    <thead>
                        <tr>
                            <th style="width: 28%;"><span>Article</span></th>
                            <th style="width: 24%;"><span>GPT-5 nano</span></th>
                            <th style="width: 24%;"><span>GPT-4o mini</span></th>
                            <th style="width: 24%;"><span>GPT-5 mini</span></th>
                        </tr>
                    </thead>
                    <tbody data-bind="foreach: threadRows">
                        <tr>
                            <td>
                                <div data-bind="text: title" style="font-weight: 600;"></div>
                                <!-- ko if: permalink -->
                                <div style="margin-top: 6px;">
                                    <a data-bind="attr: { href: permalink, title: permalink }, text: permalink"
                                        target="_blank" rel="noopener noreferrer"
                                        style="font-size: 11px; text-transform: lowercase; word-break: break-all;"></a>
                                </div>
                                <!-- /ko -->
                            </td>
                            ${modelColumns(cell)}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `;
}

export function getSeoSuggestionCellHtml(bindingPath: string): string {
    return /* html */`
        <td data-bind="with: ${bindingPath}">
            <!-- ko if: status() === 'idle' -->
                <span class="form-sub-title">—</span>
            <!-- /ko -->
            <!-- ko if: status() === 'loading' -->
                <span>Running…</span>
            <!-- /ko -->
            <!-- ko if: status() === 'error' -->
                <div style="color: #b00020;">
                    <strong>Error</strong>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <pre style="white-space: pre-wrap; margin: 4px 0 0; font-size: 11px;" data-bind="text: error"></pre>
                    <!-- ko if: rawJson -->
                    <pre style="white-space: pre-wrap; margin: 6px 0 0; font-size: 11px; max-height: 160px; overflow: auto;" data-bind="text: rawJson"></pre>
                    <!-- /ko -->
                </div>
            <!-- /ko -->
            <!-- ko if: status() === 'done' -->
                <div>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <!-- ko if: costLabel -->
                    <div class="form-sub-title" data-bind="text: costLabel"></div>
                    <!-- /ko -->
                    <!-- ko if: titleSuggestions().length -->
                    <div style="margin-top: 6px;">
                        <div class="form-sub-title" style="font-weight: 600;">SEO Title</div>
                        <ol style="margin: 4px 0 0; padding-left: 1.2rem; font-size: 12px;">
                            <!-- ko foreach: titleSuggestions -->
                            <li style="margin-bottom: 6px; white-space: pre-wrap;" data-bind="text: $data"></li>
                            <!-- /ko -->
                        </ol>
                    </div>
                    <!-- /ko -->
                    <!-- ko if: descriptionSuggestions().length -->
                    <div style="margin-top: 10px;">
                        <div class="form-sub-title" style="font-weight: 600;">SEO Description</div>
                        <ol style="margin: 4px 0 0; padding-left: 1.2rem; font-size: 12px;">
                            <!-- ko foreach: descriptionSuggestions -->
                            <li style="margin-bottom: 6px; white-space: pre-wrap;" data-bind="text: $data"></li>
                            <!-- /ko -->
                        </ol>
                    </div>
                    <!-- /ko -->
                </div>
            <!-- /ko -->
        </td>
    `;
}

export function getSeoPanelHtml(): string {
    const cell = (p: string) => getSeoSuggestionCellHtml(p);
    return /* html */`
        <div data-bind="visible: selectedArticleAssistantTab() == 'seo'">
            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-seo-brand">Brand</label>
                <div class="w-input" style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
                    <select id="model-eval-seo-brand" class="input-text"
                        data-bind="options: excerptBrands,
                                   optionsText: 'name',
                                   optionsValue: 'id',
                                   value: seoIdBrand,
                                   optionsCaption: 'Select a brand',
                                   event: { change: onChangeSeoBrand },
                                   enable: !isRunning()"
                        style="width: 100%; max-width: 420px;"></select>
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickFetchSeoFixtures, enable: !isRunning() && !!seoIdBrand()">
                        Fetch
                    </button>
                </div>
                <div class="form-sub-title" style="margin-top: 6px;">
                    Fetch loads article fixtures and that brand’s SEO prompt from AI Config (<code>seoSuggestionPrompt</code>).
                    Production returns both SEO Title and SEO Description in one call.
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-seo-system-prompt">System prompt</label>
                <div class="form-sub-title" style="margin-bottom: 6px;">
                    Loaded from the selected brand’s AI Config. Editable for testing.
                </div>
                <div class="w-input">
                    <textarea id="model-eval-seo-system-prompt" class="input-text" rows="12"
                        data-bind="value: seoSystemPrompt"
                        style="width: 100%; min-height: 220px; font-family: monospace; font-size: 12px;"></textarea>
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-seo-custom-title">Custom article title</label>
                <div class="w-input">
                    <input id="model-eval-seo-custom-title" class="input-text" type="text"
                        data-bind="value: seoCustomTitle"
                        placeholder="Paste an article title"
                        style="width: 100%;" />
                </div>
                <label class="form-label" for="model-eval-seo-custom-content" style="margin-top: 8px;">Custom article content</label>
                <div class="w-input">
                    <textarea id="model-eval-seo-custom-content" class="input-text" rows="6"
                        data-bind="value: seoCustomContent"
                        placeholder="Paste article content to evaluate against all models"
                        style="width: 100%; min-height: 120px;"></textarea>
                </div>
                <div class="list-header w-justify" style="margin-top: 8px;">
                    <div class="w-left">
                        <button class="btn highlight" type="button"
                            data-bind="click: onClickRunSeoCustom, enable: !isRunning() && !!seoCustomTitle() && !!seoCustomContent()">
                            Run custom SEO
                        </button>
                    </div>
                    <div class="w-center">
                        <span data-bind="text: seoCustomRunStatus"></span>
                    </div>
                </div>
                <div class="w-list-results" style="margin-top: 8px;">
                    <table class="list-results">
                        <thead>
                            <tr>
                                <th style="width: 28%;"><span>Custom Article</span></th>
                                <th style="width: 24%;"><span>GPT-5 nano</span></th>
                                <th style="width: 24%;"><span>GPT-4o mini</span></th>
                                <th style="width: 24%;"><span>GPT-5 mini</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr data-bind="with: seoCustomRow">
                                <td>
                                    <div data-bind="text: title" style="font-weight: 600;"></div>
                                    <div class="form-sub-title" style="margin-top: 6px; white-space: pre-wrap;" data-bind="text: contentPreview"></div>
                                </td>
                                ${modelColumns(cell)}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="list-header w-justify">
                <div class="w-left">
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickRunSeoAll, enable: !isRunning() && !!seoIdBrand() && seoRows().length > 0">
                        Run all
                    </button>
                    <button class="btn cancel" type="button" style="margin-left: 8px;"
                        data-bind="click: onClickStop, enable: isRunning()">
                        Stop
                    </button>
                </div>
                <div class="w-center">
                    <span data-bind="text: seoRunStatus"></span>
                </div>
                <div class="w-right">
                    <span data-bind="text: seoRows().length + ' fixtures'"></span>
                </div>
            </div>

            <div class="w-list-results" id="model-eval-seo-results">
                <table class="list-results">
                    <thead>
                        <tr>
                            <th style="width: 28%;"><span>Article</span></th>
                            <th style="width: 24%;"><span>GPT-5 nano</span></th>
                            <th style="width: 24%;"><span>GPT-4o mini</span></th>
                            <th style="width: 24%;"><span>GPT-5 mini</span></th>
                        </tr>
                    </thead>
                    <tbody data-bind="foreach: seoRows">
                        <tr>
                            <td>
                                <div data-bind="text: title" style="font-weight: 600;"></div>
                                <!-- ko if: permalink -->
                                <div style="margin-top: 6px;">
                                    <a data-bind="attr: { href: permalink, title: permalink }, text: permalink"
                                        target="_blank" rel="noopener noreferrer"
                                        style="font-size: 11px; text-transform: lowercase; word-break: break-all;"></a>
                                </div>
                                <!-- /ko -->
                            </td>
                            ${modelColumns(cell)}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `;
}

export function getSmoSuggestionCellHtml(bindingPath: string): string {
    return /* html */`
        <td data-bind="with: ${bindingPath}">
            <!-- ko if: status() === 'idle' -->
                <span class="form-sub-title">—</span>
            <!-- /ko -->
            <!-- ko if: status() === 'loading' -->
                <span>Running…</span>
            <!-- /ko -->
            <!-- ko if: status() === 'error' -->
                <div style="color: #b00020;">
                    <strong>Error</strong>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <pre style="white-space: pre-wrap; margin: 4px 0 0; font-size: 11px;" data-bind="text: error"></pre>
                    <!-- ko if: rawJson -->
                    <pre style="white-space: pre-wrap; margin: 6px 0 0; font-size: 11px; max-height: 160px; overflow: auto;" data-bind="text: rawJson"></pre>
                    <!-- /ko -->
                </div>
            <!-- /ko -->
            <!-- ko if: status() === 'done' -->
                <div>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <!-- ko if: costLabel -->
                    <div class="form-sub-title" data-bind="text: costLabel"></div>
                    <!-- /ko -->
                    <!-- ko if: titleSuggestions().length -->
                    <div style="margin-top: 6px;">
                        <div class="form-sub-title" style="font-weight: 600;">SMO Title</div>
                        <ol style="margin: 4px 0 0; padding-left: 1.2rem; font-size: 12px;">
                            <!-- ko foreach: titleSuggestions -->
                            <li style="margin-bottom: 6px; white-space: pre-wrap;" data-bind="text: $data"></li>
                            <!-- /ko -->
                        </ol>
                    </div>
                    <!-- /ko -->
                    <!-- ko if: descriptionSuggestions().length -->
                    <div style="margin-top: 10px;">
                        <div class="form-sub-title" style="font-weight: 600;">SMO Description</div>
                        <ol style="margin: 4px 0 0; padding-left: 1.2rem; font-size: 12px;">
                            <!-- ko foreach: descriptionSuggestions -->
                            <li style="margin-bottom: 6px; white-space: pre-wrap;" data-bind="text: $data"></li>
                            <!-- /ko -->
                        </ol>
                    </div>
                    <!-- /ko -->
                    <!-- ko if: tagLineSuggestions().length -->
                    <div style="margin-top: 10px;">
                        <div class="form-sub-title" style="font-weight: 600;">Social Tagline</div>
                        <ol style="margin: 4px 0 0; padding-left: 1.2rem; font-size: 12px;">
                            <!-- ko foreach: tagLineSuggestions -->
                            <li style="margin-bottom: 6px; white-space: pre-wrap;" data-bind="text: $data"></li>
                            <!-- /ko -->
                        </ol>
                    </div>
                    <!-- /ko -->
                </div>
            <!-- /ko -->
        </td>
    `;
}

export function getSmoPanelHtml(): string {
    const cell = (p: string) => getSmoSuggestionCellHtml(p);
    return /* html */`
        <div data-bind="visible: selectedArticleAssistantTab() == 'smo'">
            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-smo-brand">Brand</label>
                <div class="w-input" style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
                    <select id="model-eval-smo-brand" class="input-text"
                        data-bind="options: excerptBrands,
                                   optionsText: 'name',
                                   optionsValue: 'id',
                                   value: smoIdBrand,
                                   optionsCaption: 'Select a brand',
                                   event: { change: onChangeSmoBrand },
                                   enable: !isRunning()"
                        style="width: 100%; max-width: 420px;"></select>
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickFetchSmoFixtures, enable: !isRunning() && !!smoIdBrand()">
                        Fetch
                    </button>
                </div>
                <div class="form-sub-title" style="margin-top: 6px;">
                    Fetch loads article fixtures and that brand’s Social Media prompt from AI Config (<code>socialmediaSuggestionPrompt</code>).
                    Production returns SMO Title, SMO Description, and Social Tagline in one call.
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-smo-system-prompt">System prompt</label>
                <div class="form-sub-title" style="margin-bottom: 6px;">
                    Loaded from the selected brand’s AI Config. Editable for testing.
                </div>
                <div class="w-input">
                    <textarea id="model-eval-smo-system-prompt" class="input-text" rows="12"
                        data-bind="value: smoSystemPrompt"
                        style="width: 100%; min-height: 220px; font-family: monospace; font-size: 12px;"></textarea>
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-smo-custom-title">Custom article title</label>
                <div class="w-input">
                    <input id="model-eval-smo-custom-title" class="input-text" type="text"
                        data-bind="value: smoCustomTitle"
                        placeholder="Paste an article title"
                        style="width: 100%;" />
                </div>
                <label class="form-label" for="model-eval-smo-custom-content" style="margin-top: 8px;">Custom article content</label>
                <div class="w-input">
                    <textarea id="model-eval-smo-custom-content" class="input-text" rows="6"
                        data-bind="value: smoCustomContent"
                        placeholder="Paste article content to evaluate against all models"
                        style="width: 100%; min-height: 120px;"></textarea>
                </div>
                <div class="list-header w-justify" style="margin-top: 8px;">
                    <div class="w-left">
                        <button class="btn highlight" type="button"
                            data-bind="click: onClickRunSmoCustom, enable: !isRunning() && !!smoCustomTitle() && !!smoCustomContent()">
                            Run custom SMO
                        </button>
                    </div>
                    <div class="w-center">
                        <span data-bind="text: smoCustomRunStatus"></span>
                    </div>
                </div>
                <div class="w-list-results" style="margin-top: 8px;">
                    <table class="list-results">
                        <thead>
                            <tr>
                                <th style="width: 28%;"><span>Custom Article</span></th>
                                <th style="width: 24%;"><span>GPT-5 nano</span></th>
                                <th style="width: 24%;"><span>GPT-4o mini</span></th>
                                <th style="width: 24%;"><span>GPT-5 mini</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr data-bind="with: smoCustomRow">
                                <td>
                                    <div data-bind="text: title" style="font-weight: 600;"></div>
                                    <div class="form-sub-title" style="margin-top: 6px; white-space: pre-wrap;" data-bind="text: contentPreview"></div>
                                </td>
                                ${modelColumns(cell)}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="list-header w-justify">
                <div class="w-left">
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickRunSmoAll, enable: !isRunning() && !!smoIdBrand() && smoRows().length > 0">
                        Run all
                    </button>
                    <button class="btn cancel" type="button" style="margin-left: 8px;"
                        data-bind="click: onClickStop, enable: isRunning()">
                        Stop
                    </button>
                </div>
                <div class="w-center">
                    <span data-bind="text: smoRunStatus"></span>
                </div>
                <div class="w-right">
                    <span data-bind="text: smoRows().length + ' fixtures'"></span>
                </div>
            </div>

            <div class="w-list-results" id="model-eval-smo-results">
                <table class="list-results">
                    <thead>
                        <tr>
                            <th style="width: 28%;"><span>Article</span></th>
                            <th style="width: 24%;"><span>GPT-5 nano</span></th>
                            <th style="width: 24%;"><span>GPT-4o mini</span></th>
                            <th style="width: 24%;"><span>GPT-5 mini</span></th>
                        </tr>
                    </thead>
                    <tbody data-bind="foreach: smoRows">
                        <tr>
                            <td>
                                <div data-bind="text: title" style="font-weight: 600;"></div>
                                <!-- ko if: permalink -->
                                <div style="margin-top: 6px;">
                                    <a data-bind="attr: { href: permalink, title: permalink }, text: permalink"
                                        target="_blank" rel="noopener noreferrer"
                                        style="font-size: 11px; text-transform: lowercase; word-break: break-all;"></a>
                                </div>
                                <!-- /ko -->
                            </td>
                            ${modelColumns(cell)}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `;
}

export function getFactCheckCellHtml(bindingPath: string): string {
    return /* html */`
        <td data-bind="with: ${bindingPath}">
            <!-- ko if: status() === 'idle' -->
                <span class="form-sub-title">—</span>
            <!-- /ko -->
            <!-- ko if: status() === 'loading' -->
                <span>Running…</span>
            <!-- /ko -->
            <!-- ko if: status() === 'error' -->
                <div style="color: #b00020;">
                    <strong>Error</strong>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <pre style="white-space: pre-wrap; margin: 4px 0 0; font-size: 11px;" data-bind="text: error"></pre>
                    <!-- ko if: rawJson -->
                    <pre style="white-space: pre-wrap; margin: 6px 0 0; font-size: 11px; max-height: 160px; overflow: auto;" data-bind="text: rawJson"></pre>
                    <!-- /ko -->
                </div>
            <!-- /ko -->
            <!-- ko if: status() === 'done' -->
                <div>
                    <!-- ko if: durationLabel -->
                    <div class="form-sub-title" data-bind="text: durationLabel"></div>
                    <!-- /ko -->
                    <!-- ko if: costLabel -->
                    <div class="form-sub-title" data-bind="text: costLabel"></div>
                    <!-- /ko -->
                    <div style="margin-top: 6px; font-size: 12px; max-height: 280px; overflow: auto;"
                        data-bind="html: contentHtml"></div>
                    <!-- ko if: searchResults().length -->
                    <div style="margin-top: 10px;">
                        <div class="form-sub-title" style="font-weight: 600;">Sources</div>
                        <ol style="margin: 4px 0 0; padding-left: 1.2rem; font-size: 11px;">
                            <!-- ko foreach: searchResults -->
                            <li style="margin-bottom: 4px; word-break: break-all;">
                                <a data-bind="attr: { href: url, title: title }, text: title || url"
                                    target="_blank" rel="noopener noreferrer"></a>
                            </li>
                            <!-- /ko -->
                        </ol>
                    </div>
                    <!-- /ko -->
                </div>
            <!-- /ko -->
        </td>
    `;
}

export function getFactCheckPanelHtml(): string {
    const cell = (p: string) => getFactCheckCellHtml(p);
    return /* html */`
        <div data-bind="visible: selectedArticleAssistantTab() == 'fact_checking'">
            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-fact-check-brand">Brand</label>
                <div class="w-input" style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
                    <select id="model-eval-fact-check-brand" class="input-text"
                        data-bind="options: excerptBrands,
                                   optionsText: 'name',
                                   optionsValue: 'id',
                                   value: factCheckIdBrand,
                                   optionsCaption: 'Select a brand',
                                   event: { change: onChangeFactCheckBrand },
                                   enable: !isRunning()"
                        style="width: 100%; max-width: 420px;"></select>
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickFetchFactCheckFixtures, enable: !isRunning() && !!factCheckIdBrand()">
                        Fetch
                    </button>
                </div>
                <div class="form-sub-title" style="margin-top: 6px;">
                    Fetch loads article fixtures and that brand’s Fact Check prompt from AI Config (<code>factCheckSuggestionPrompt</code>).
                    Production uses Perplexity Sonar. OpenAI columns use the Responses API <code>web_search</code> tool;
                    both return freeform HTML plus sources when available.
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-fact-check-system-prompt">System prompt</label>
                <div class="form-sub-title" style="margin-bottom: 6px;">
                    Loaded from the selected brand’s AI Config. Editable for testing.
                </div>
                <div class="w-input">
                    <textarea id="model-eval-fact-check-system-prompt" class="input-text" rows="12"
                        data-bind="value: factCheckSystemPrompt"
                        style="width: 100%; min-height: 220px; font-family: monospace; font-size: 12px;"></textarea>
                </div>
            </div>

            <div class="form-group" style="margin-bottom: 16px;">
                <label class="form-label" for="model-eval-fact-check-custom-title">Custom article title</label>
                <div class="w-input">
                    <input id="model-eval-fact-check-custom-title" class="input-text" type="text"
                        data-bind="value: factCheckCustomTitle"
                        placeholder="Optional label for this custom run"
                        style="width: 100%;" />
                </div>
                <label class="form-label" for="model-eval-fact-check-custom-content" style="margin-top: 8px;">Custom article content</label>
                <div class="w-input">
                    <textarea id="model-eval-fact-check-custom-content" class="input-text" rows="6"
                        data-bind="value: factCheckCustomContent"
                        placeholder="Paste article content to evaluate against all models (title is not sent to the model)"
                        style="width: 100%; min-height: 120px;"></textarea>
                </div>
                <div class="list-header w-justify" style="margin-top: 8px;">
                    <div class="w-left">
                        <button class="btn highlight" type="button"
                            data-bind="click: onClickRunFactCheckCustom, enable: !isRunning() && !!factCheckCustomContent()">
                            Run custom Fact Check
                        </button>
                    </div>
                    <div class="w-center">
                        <span data-bind="text: factCheckCustomRunStatus"></span>
                    </div>
                </div>
                <div class="w-list-results" style="margin-top: 8px;">
                    <table class="list-results">
                        <thead>
                            <tr>
                                <th style="width: 28%;"><span>Custom Article</span></th>
                                <th style="width: 18%;"><span>Perplexity Sonar</span></th>
                                <th style="width: 18%;"><span>GPT-5 nano</span></th>
                                <th style="width: 18%;"><span>GPT-4.1 mini</span></th>
                                <th style="width: 18%;"><span>GPT-5 mini</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr data-bind="with: factCheckCustomRow">
                                <td>
                                    <div data-bind="text: title" style="font-weight: 600;"></div>
                                    <div class="form-sub-title" style="margin-top: 6px; white-space: pre-wrap;" data-bind="text: contentPreview"></div>
                                </td>
                                ${factCheckModelColumns(cell)}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="list-header w-justify">
                <div class="w-left">
                    <button class="btn highlight" type="button"
                        data-bind="click: onClickRunFactCheckAll, enable: !isRunning() && !!factCheckIdBrand() && factCheckRows().length > 0">
                        Run all
                    </button>
                    <button class="btn cancel" type="button" style="margin-left: 8px;"
                        data-bind="click: onClickStop, enable: isRunning()">
                        Stop
                    </button>
                </div>
                <div class="w-center">
                    <span data-bind="text: factCheckRunStatus"></span>
                </div>
                <div class="w-right">
                    <span data-bind="text: factCheckRows().length + ' fixtures'"></span>
                </div>
            </div>

            <div class="w-list-results" id="model-eval-fact-check-results">
                <table class="list-results">
                    <thead>
                        <tr>
                            <th style="width: 28%;"><span>Article</span></th>
                            <th style="width: 18%;"><span>Perplexity Sonar</span></th>
                            <th style="width: 18%;"><span>GPT-5 nano</span></th>
                            <th style="width: 18%;"><span>GPT-4.1 mini</span></th>
                            <th style="width: 18%;"><span>GPT-5 mini</span></th>
                        </tr>
                    </thead>
                    <tbody data-bind="foreach: factCheckRows">
                        <tr>
                            <td>
                                <div data-bind="text: title" style="font-weight: 600;"></div>
                                <!-- ko if: permalink -->
                                <div style="margin-top: 6px;">
                                    <a data-bind="attr: { href: permalink, title: permalink }, text: permalink"
                                        target="_blank" rel="noopener noreferrer"
                                        style="font-size: 11px; text-transform: lowercase; word-break: break-all;"></a>
                                </div>
                                <!-- /ko -->
                            </td>
                            ${factCheckModelColumns(cell)}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `;
}



// WEBPACK FOOTER //
// ./js/components/templates/model_eval_taxonomy_ui.ts